/**
 * CompareSubmenuRowView - Presentational component for compare submenu rows
 *
 * Renders a selectable row for submenu items (players, watchlists, etc.)
 * Shows item name, team, and position badge when available.
 * @remarks This is a View component - contains no business logic, hooks, or state
 */

import React from "react";

import { Box } from "@/components/atoms/Box";
import { Pressable } from "@/components/atoms/Pressable";
import { Text } from "@/components/atoms/Text";
import { Stack } from "@/components/atoms/Stack";

import type { CompareItem } from "@/features/player-detail/types";

/**
 * Shared identity for every compare-submenu result row.
 *
 * Deliberately NOT keyed on the item id: SE-7111's regression spec needs to
 * COUNT the rows a search returned, which is what distinguishes one capped
 * page from a paginated list, and a per-item id gives nothing to count.
 */
const COMPARE_SUBMENU_ROW_TEST_ID = "compare-submenu-row";

/**
 * Props for CompareSubmenuRowView component
 */
interface CompareSubmenuRowViewProps {
  /** The compare item to display */
  readonly item: CompareItem;
  /** Whether dark mode is active */
  readonly isDark: boolean;
  /** Handler for pressing the row */
  readonly onPress: () => void;
}

/**
 * CompareSubmenuRowView renders a selectable submenu item row
 * @param props - View props
 * @param props.item - The compare item to display
 * @param props.isDark - Whether dark mode is active
 * @param props.onPress - Handler for pressing the row
 * @returns Pressable row with item name, team, and position
 */
const CompareSubmenuRowView = ({
  item,
  onPress,
}: CompareSubmenuRowViewProps) => (
  <Pressable
    onPress={onPress}
    testID={COMPARE_SUBMENU_ROW_TEST_ID}
    className="relative px-5 py-3 active:bg-surface-raised"
  >
    <Stack className="gap-micro pr-16">
      <Text variant="title-sm-medium" className="text-content-primary">
        {item.name}
      </Text>
      {item.team && (
        <Text variant="body" className="text-content-muted">
          {item.team}
        </Text>
      )}
    </Stack>
    {item.position && (
      <Box className="absolute right-5 top-1/2 rounded-sm bg-surface-subtle px-3 py-1 -translate-y-1/2">
        <Text variant="body-strong" className="text-content-secondary">
          {item.position}
        </Text>
      </Box>
    )}
  </Pressable>
);

CompareSubmenuRowView.displayName = "CompareSubmenuRowView";

export default React.memo(CompareSubmenuRowView);
